import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { studentApi } from '@/lib/api';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    book: <svg {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" /></svg>,
    award: <svg {...p}><circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" /></svg>,
    clock: <svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    play: <svg {...p}><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill={color} stroke="none" /></svg>,
    arrow: <svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilStudentDashboard() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const { data, isLoading } = useQuery({
    queryKey: ['student-dashboard'],
    queryFn: () => studentApi.getDashboard(),
  });

  if (isLoading) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-mut)' }}>Loading...</span>
      </div>
    );
  }

  const courses = data?.courses || [];
  const stats = data?.stats || {} as any;

  return (
    <div>
      <h1 style={{ fontFamily: S.heading, fontSize: 32, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 28 }}>
        {t('dashboard.title')}
      </h1>

      {/* Stats */}
      <div className="soleil-grid-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 32 }}>
        {([
          [stats.totalCourses || 0, t('dashboard.stats.enrolled'), 'book', 'var(--soleil-gold)', 'var(--soleil-gold-pale)'],
          [stats.completedCourses || 0, t('dashboard.stats.completed'), 'award', 'var(--soleil-olive)', 'var(--soleil-olive-pale)'],
          [stats.inProgressCourses || 0, t('dashboard.stats.inProgress'), 'clock', 'var(--soleil-sienna)', 'var(--soleil-sienna-pale)'],
        ] as [number, string, string, string, string][]).map(([val, label, ic, color, bg], i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 16, padding: '24px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 13, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ic type={ic} size={22} color={color} />
            </div>
            <div>
              <div style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)' }}>{val}</div>
              <div style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)' }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* My Courses */}
      <div style={{ background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 18, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--soleil-sand)' }}>
          <h2 style={{ fontFamily: S.heading, fontSize: 22, fontWeight: 600, color: 'var(--soleil-ink)' }}>{t('dashboard.myCourses')}</h2>
        </div>
        <div style={{ padding: 8 }}>
          {courses.length > 0 ? (
            courses.map((course: any) => {
              const progress = course.enrollment?.progressPercent || 0;
              const isDone = progress === 100;
              const isNew = progress === 0;
              return (
                <div key={course.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '18px 16px', borderRadius: 12, margin: 4,
                  transition: 'background 0.15s',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontFamily: S.font, fontSize: 15, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 6 }}>
                      {isRTL ? course.titleAr : course.titleEn}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)' }}>
                      <span>{isRTL ? 'التقدم' : 'Progress'}: {progress}%</span>
                      {course.completedLessons !== undefined && course.totalLessons !== undefined && (
                        <span>{course.completedLessons} / {course.totalLessons} {isRTL ? 'درس' : 'lessons'}</span>
                      )}
                    </div>
                    {progress > 0 && (
                      <div style={{ marginTop: 8, width: '100%', height: 6, background: 'var(--soleil-linen)', borderRadius: 100, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 100, transition: 'width 0.4s ease',
                          width: `${progress}%`,
                          background: isDone
                            ? 'linear-gradient(90deg, var(--soleil-olive), #8FA85E)'
                            : 'linear-gradient(90deg, var(--soleil-gold), var(--soleil-gold-light))',
                        }} />
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/dashboard/courses/${course.id}`}
                    style={{
                      marginLeft: 16, padding: '10px 22px', borderRadius: 10,
                      fontFamily: S.font, fontSize: 13, fontWeight: 600, textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      ...(isDone
                        ? { background: 'var(--soleil-olive-pale)', color: 'var(--soleil-olive)', border: '1px solid rgba(122,140,82,0.2)' }
                        : { background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', color: 'var(--soleil-dark)', border: 'none', boxShadow: '0 2px 10px rgba(184,148,58,0.2)' }),
                    }}
                  >
                    <Ic type={isDone ? 'award' : 'play'} size={14} color={isDone ? 'var(--soleil-olive)' : 'var(--soleil-dark)'} />
                    {isDone
                      ? (t('dashboard.completed') || 'Completed')
                      : isNew
                        ? (t('dashboard.start') || 'Start Learning')
                        : (t('dashboard.continue') || 'Continue')}
                  </Link>
                </div>
              );
            })
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-mut)', marginBottom: 16 }}>{t('dashboard.noCourses')}</p>
              <Link to="/courses" style={{
                fontFamily: S.font, fontSize: 14, fontWeight: 600, color: 'var(--soleil-dark)',
                background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
                padding: '12px 28px', borderRadius: 10, textDecoration: 'none',
              }}>
                {t('dashboard.browseCourses')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
